import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchAllReports, Report } from '../services/reportService';
import { Loader2, FileText, Download, Eye, Calendar } from 'lucide-react';

export const Reports: React.FC = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      const data = await fetchAllReports();
      setReports(data);
      setLoading(false);
    };
    loadData();
  }, []);

  return (
    <div className="bg-slate-50 min-h-screen">
      <Helmet>
        <title>Rapports d'Activités | Collectif LAMUKA</title>
        <meta name="description" content="Consultez et téléchargez les rapports d'activités officiels du Collectif LAMUKA." />
      </Helmet>

      <section className="relative py-24 md:py-32 bg-slate-900 text-white overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-pink-600/20 rounded-full blur-[100px] animate-pulse -translate-y-1/2 translate-x-1/3"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-600/20 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4"></div>
        
        <div className="container mx-auto px-4 relative z-10 text-center space-y-8 animate-slide-up">
          <span className="inline-block py-1 px-4 rounded-full bg-pink-500/20 border border-pink-500/50 text-pink-300 text-[10px] font-black tracking-widest uppercase">
            Transparence & Bilan
          </span>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-black mb-8 leading-tight tracking-tighter">
            Nos Rapports <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-400 uppercase">d'Activités</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed font-medium">
             Consultez nos rapports officiels pour suivre l'impact et l'évolution de nos actions en faveur de l'inclusion.
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-20 pb-40">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-10 h-10 text-pink-600 animate-spin mb-4" />
            <p className="text-slate-500 font-medium tracking-widest text-[10px] uppercase">Chargement des rapports...</p>
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-[3rem] shadow-sm border border-slate-100">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-6" />
            <h3 className="text-2xl font-black text-slate-900 mb-2">Aucun rapport pour le moment</h3>
            <p className="text-slate-500">Les rapports d'activités publiés apparaîtront ici.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {reports.map((report) => (
              <div key={report.id} className="group bg-white rounded-[2.5rem] p-10 shadow-lg border border-slate-100 hover:shadow-2xl hover:border-pink-100 transition-all duration-500 flex flex-col transform hover:-translate-y-2">
                <div className="w-16 h-16 bg-pink-50 text-pink-600 rounded-2xl flex items-center justify-center mb-8 group-hover:scale-110 group-hover:bg-pink-600 group-hover:text-white transition-all shadow-sm">
                  <FileText className="w-8 h-8" />
                </div>
                <div className="flex items-center text-[10px] text-slate-400 font-black uppercase tracking-widest mb-4 gap-4">
                    <span className="flex items-center"><Calendar className="w-3.5 h-3.5 mr-2 text-pink-500" /> {report.date}</span>
                </div>
                <h3 className="text-2xl font-black text-slate-900 mb-4 leading-tight group-hover:text-pink-600 transition-colors">
                  {report.title}
                </h3>
                <p className="text-slate-500 mb-8 line-clamp-3 text-sm leading-relaxed flex-1">
                  {report.description}
                </p>
                <div className="flex items-center gap-4 pt-8 border-t border-slate-50 mt-auto">
                  <a 
                    href={report.file_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex-1 bg-slate-900 text-white text-center py-4 rounded-[1.5rem] font-black text-xs uppercase tracking-widest hover:bg-slate-800 transition-colors shadow-lg flex justify-center items-center gap-2"
                  >
                    <Eye className="w-4 h-4" /> Consulter
                  </a>
                  <a 
                    href={report.file_url} 
                    download={report.file_name}
                    target="_blank"
                    className="p-4 bg-pink-50 text-pink-600 rounded-[1.5rem] hover:bg-pink-600 hover:text-white transition-colors border border-pink-100 shadow-sm flex items-center justify-center"
                    title="Télécharger"
                  >
                    <Download className="w-5 h-5" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <style>{`
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slide-up { animation: slide-up 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
      `}</style>
    </div>
  );
};
